import { getProductCollectionRealTimeInventory } from "@insite/client-framework/Services/ProductServiceV2";
import { ProductInventoryDto } from "@insite/client-framework/Types/ApiModels";
import { useEffect, useState } from "react";

interface CacheEntry {
    promise: Promise<ProductInventoryDto | undefined>;
    result?: ProductInventoryDto;
}

const inventoryCache = new Map<string, CacheEntry>();

function fetchInventory(productId: string): CacheEntry {
    const existing = inventoryCache.get(productId);
    if (existing) {
        return existing;
    }

    const promise = getProductCollectionRealTimeInventory({
        productIds: [productId],
    }).then(response => {
        const inventory = response.realTimeInventoryResults?.find(r => r.productId === productId);
        const entry = inventoryCache.get(productId);
        if (entry) {
            entry.result = inventory;
        }
        return inventory;
    });

    const entry: CacheEntry = { promise };
    inventoryCache.set(productId, entry);
    return entry;
}

/**
 * Shared hook that fetches per-product real-time inventory once per product ID.
 * Multiple components calling this with the same productId share a single API request.
 */
export default function useOnDemandInventory(productId: string | undefined): ProductInventoryDto | undefined {
    const [inventory, setInventory] = useState<ProductInventoryDto | undefined>(() => {
        if (!productId) {
            return undefined;
        }
        return inventoryCache.get(productId)?.result;
    });

    useEffect(() => {
        if (!productId) {
            setInventory(undefined);
            return;
        }

        const entry = fetchInventory(productId);

        if (entry.result) {
            setInventory(entry.result);
            return;
        }

        let cancelled = false;
        entry.promise.then(result => {
            if (!cancelled) {
                setInventory(result);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [productId]);

    // Clean up stale cache entries when product changes
    useEffect(() => {
        return () => {
            if (productId) {
                inventoryCache.delete(productId);
            }
        };
    }, [productId]);

    return inventory;
}
